"use client"

import { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import ProductServiceService from "@/api/productservices.service"
import { Input } from "@/components/ui/input"
import { SendIcon, Loader2, ArrowLeft } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { Textarea } from "@/components/ui/textarea"

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().max(255, "Description must be less than 255 characters"),
  unitPrice: z.number().min(0, "Price must be 0 or greater"),
  currency: z.string().length(3, "Currency must be a 3-letter code")
})

export type ProductService = {
  id: number
  publicId: string
  name: string
  description?: string
  unitPrice: number
  currency: string
  dateUpdated?: Date
  dateCreated?: Date
}

export default function CreateProductServiceForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      unitPrice: 0,
      currency: "USD"
    },
  })

  const { toast } = useToast()

  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    })
  }

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null
    return storedBusiness ? JSON.parse(storedBusiness) : null
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true)
    try {
      const response = await ProductServiceService.addProductService(selectedBusiness.id, values)
      const result = response.data
      if (result.result === "success") {
        showToastWithTimeout("success", "✅ Success", "Product/Service added successfully", 3000)
        form.reset()
      } else {
        showToastWithTimeout("error", "❌ Error", result.message || "Failed to add product/service", 3000)
      }
    } catch (error) {
      console.error(error)
      showToastWithTimeout("error", "❌ Error", "Failed to add product/service", 3000)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="container py-10 mx-auto bg-white px-10 rounded-lg">
      <h2 className="text-3xl font-bold tracking-tight">Add New Product/Service</h2>
      <div className='flex items-start align-middle mb-5'>
        <Button onClick={() => window.location.href = '/product-services'} className='mr-2'> 
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back</Button>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid sm:grid-cols-2 grid-cols-1 gap-4">

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name <span className="text-red-700">*</span></FormLabel>
                  <FormControl>
                    <Input placeholder="Product/Service Name" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the product or service name
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Describe the product or service" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter a detailed description
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="unitPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit Price <span className="text-red-700">*</span></FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder="0" 
                      {...field}
                      onChange={e => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter the price per unit
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency <span className="text-red-700">*</span></FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        {/* Add more currencies as needed */}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormDescription>
                    Select the currency
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

          </div>
          <div className="flex justify-end">
            <Button type="submit" className="bg-[#0E415E] text-white">
              {isSubmitting ? 
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating...</> : 
                <><SendIcon className="w-4 h-4 mr-2" /> Create Product/Service</>
              }
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
